import React, { useEffect, useState } from "react";
import axios from "axios";
import { Atom, Activity, Zap, Shield, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import ProgressRing from "./ProgressRing";
import AnimatedNumber from "./AnimatedNumber";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const REGIME_STYLE = {
  STRUCTURED:   { color: "#4dffaf", bg: "rgba(77,255,175,0.08)", border: "rgba(77,255,175,0.25)", label: "STRUCTURED" },
  TRANSITIONAL: { color: "#ffc24d", bg: "rgba(255,194,77,0.08)", border: "rgba(255,194,77,0.25)", label: "TRANSITIONAL" },
  CHAOTIC:      { color: "#ff5577", bg: "rgba(255,85,119,0.08)", border: "rgba(255,85,119,0.25)", label: "CHAOTIC" },
};

const DETECTOR_LABELS = {
  netflow_momentum_spike: "Netflow Spike (SG)",
  oi_divergence:          "OI Divergence",
  funding_extreme:        "Funding Extreme",
  orderbook_imbalance:    "Orderbook Imbalance",
  regime_chaos:           "Regime Chaos",
  cascade_correlation:    "Cascade Correlation",
  variance_explosion:     "Variance Explosion",
};

function RegimeChip({ regime }) {
  const s = REGIME_STYLE[regime] || REGIME_STYLE.TRANSITIONAL;
  return (
    <span className="rounded-full px-2.5 py-0.5 text-[9px] uppercase tracking-[0.22em] font-semibold"
          style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5">
      <div className="text-[9px] uppercase tracking-[0.22em] text-white/45 mb-1">{label}</div>
      <div className="font-mono text-sm font-semibold tabular-nums" style={{ color: color || "#e2e8f0" }}>
        {value}
      </div>
    </div>
  );
}

function HurstBar({ label, value, classification }) {
  const pct = Math.min(100, Math.max(0, Number(value) * 100));
  const color = classification === "trending" ? "#4dffaf"
              : classification === "ranging"  ? "#5cf2ff"
              : "#94a3b8";
  const Icon = classification === "trending" ? TrendingUp
             : classification === "ranging"  ? TrendingDown
             : Minus;
  return (
    <div className="flex items-center gap-3">
      <div className="text-[9px] uppercase tracking-[0.18em] text-white/55 w-5">{label}</div>
      <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-700"
             style={{ width: `${pct}%`, background: color, boxShadow: `0 0 6px ${color}` }} />
      </div>
      <div className="font-mono text-[10px] tabular-nums" style={{ color }}>{Number(value).toFixed(3)}</div>
      <Icon className="h-3 w-3 flex-shrink-0" style={{ color }} />
    </div>
  );
}

function DetectorDot({ name, active }) {
  const label = DETECTOR_LABELS[name] || name.replace(/_/g, " ");
  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5">
      {active
        ? <AlertTriangle className="h-3 w-3 text-amber-400 flex-shrink-0" />
        : <CheckCircle className="h-3 w-3 text-emerald-400/60 flex-shrink-0" />}
      <span className="text-[10px] text-white/65 capitalize">{label}</span>
      {active && <span className="ml-auto text-[9px] text-amber-400 uppercase tracking-wide">firing</span>}
    </div>
  );
}

export default function QuantumPhysicsPage() {
  const [quantState, setQuantState] = useState(null);
  const [gate, setGate] = useState(null);
  const [oracle, setOracle] = useState(null);
  const [nexusAwareness, setNexusAwareness] = useState(null);
  const [nexusConfig, setNexusConfig] = useState(null);
  const [anomalyFlags, setAnomalyFlags] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [qs, g, ora, na, nc, af] = await Promise.allSettled([
          axios.get(`${API}/quantum/state`),
          axios.get(`${API}/quantum/identity/gate`),
          axios.get(`${API}/oracle-entropy/status`),
          axios.get(`${API}/nexus/awareness`),
          axios.get(`${API}/nexus/config`),
          axios.get(`${API}/quant/anomaly-flags`),
        ]);
        if (!alive) return;
        if (qs.status === "fulfilled") setQuantState(qs.value.data);
        if (g.status  === "fulfilled") setGate(g.value.data);
        if (ora.status === "fulfilled") setOracle(ora.value.data);
        if (na.status === "fulfilled") setNexusAwareness(na.value.data);
        if (nc.status === "fulfilled") setNexusConfig(nc.value.data);
        if (af.status === "fulfilled") setAnomalyFlags(af.value.data?.flags || []);
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const engineStates = quantState?.engine_states || [];
  const H = Number(quantState?.H || 0);
  const C = Number(quantState?.C || 0);
  const pm = Number(quantState?.position_multiplier || 0);

  const oracleRegime = oracle?.regime || "TRANSITIONAL";
  const H_market = Number(oracle?.H_market || 0);
  const H_structured = Number(oracle?.thresholds?.structured || 0.79);
  const H_chaotic = Number(oracle?.thresholds?.chaotic || 0.84);

  const gateOpen = gate?.gate_open ?? true;
  const gateDecision = gate?.decision || "ALLOW";
  const H_gate = Number(gate?.H_value || H);

  const hurst_mtf = nexusAwareness?.hurst_mtf || {};
  const confluenceScore = Number(nexusAwareness?.hurst_confluence ?? 0);

  const nexusPaused = nexusConfig?.pause ?? false;
  const nexusModifier = Number(nexusConfig?.position_modifier ?? 1);
  const nexusRegime = nexusConfig?.regime || "UNKNOWN";

  const activeFlags = new Set(anomalyFlags.filter(f => {
    const exp = f.expires_at;
    if (!exp) return false;
    return new Date(exp) > new Date();
  }).map(f => f.flag_type));

  const allDetectors = Object.keys(DETECTOR_LABELS);

  return (
    <div className="space-y-4">

      {/* HEADER */}
      <div className="flex items-center gap-2 px-1">
        <Atom className="h-4 w-4 text-violet-300" />
        <h2 className="font-display text-base font-semibold text-white">Quantum & Physics</h2>
        <span className="ml-auto text-[9px] uppercase tracking-[0.22em] text-white/35">live · 30s</span>
      </div>

      {/* ROW 1 — Oracle Entropy + H-Gate */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

        {/* Oracle Entropy Gate */}
        <section className="arena-equity-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity className="h-3.5 w-3.5 text-cyan-300" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-white/70">Oracle Entropy</span>
            </div>
            <RegimeChip regime={oracleRegime} />
          </div>

          <div className="flex items-center gap-4 mb-3">
            <ProgressRing
              pct={Math.min(100, H_market * 100)}
              size={64}
              stroke={5}
              color={REGIME_STYLE[oracleRegime]?.color || "#ffc24d"}
            >
              <div className="text-center">
                <div className="text-sm font-bold tabular-nums">{H_market.toFixed(3)}</div>
                <div className="text-[8px] text-white/40 uppercase">H_mkt</div>
              </div>
            </ProgressRing>
            <div className="flex-1 space-y-1.5">
              <div className="flex justify-between text-[10px] text-white/50">
                <span>Structured &lt; {H_structured.toFixed(2)}</span>
                <span className="text-emerald-400">🟢</span>
              </div>
              <div className="flex justify-between text-[10px] text-white/50">
                <span>Transitional &lt; {H_chaotic.toFixed(2)}</span>
                <span className="text-amber-400">🟡</span>
              </div>
              <div className="flex justify-between text-[10px] text-white/50">
                <span>Chaotic ≥ {H_chaotic.toFixed(2)}</span>
                <span className="text-rose-400">🔴</span>
              </div>
            </div>
          </div>

          {/* Per-asset entropy */}
          {oracle?.per_asset && Object.keys(oracle.per_asset).length > 0 && (
            <div className="grid grid-cols-5 gap-1 mt-2">
              {Object.entries(oracle.per_asset).map(([asset, data]) => (
                <div key={asset} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-1.5 text-center">
                  <div className="text-[8px] text-white/40 uppercase">{asset}</div>
                  <div className="text-[10px] font-mono font-semibold text-white/80">{Number(data.H_j).toFixed(3)}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* H-Gate + Quantum Health */}
        <section className="arena-equity-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Shield className="h-3.5 w-3.5 text-violet-300" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-white/70">Quantum H-Gate</span>
            </div>
            <span className={`rounded-full px-2.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-semibold ${gateOpen ? "text-emerald-400 bg-emerald-400/10 border border-emerald-400/30" : "text-rose-400 bg-rose-400/10 border border-rose-400/30"}`}>
              {gateDecision}
            </span>
          </div>

          <div className="flex items-center gap-4 mb-4">
            <ProgressRing pct={Math.min(100, H_gate * 100)} size={64} stroke={5} color={gateOpen ? "#4dffaf" : "#ff5577"}>
              <div className="text-center">
                <div className="text-sm font-bold tabular-nums">{H_gate.toFixed(3)}</div>
                <div className="text-[8px] text-white/40 uppercase">H</div>
              </div>
            </ProgressRing>
            <div className="flex-1 space-y-1.5 text-[10px]">
              <div className="flex justify-between text-white/50">
                <span>Threshold</span>
                <span className="font-mono text-white/70">{Number(gate?.threshold || 0.1).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-white/50">
                <span>Coherence C</span>
                <span className="font-mono text-cyan-300">{C.toFixed(3)}</span>
              </div>
              <div className="flex justify-between text-white/50">
                <span>Position ×</span>
                <span className="font-mono text-amber-300">{pm.toFixed(3)}</span>
              </div>
            </div>
          </div>

          {/* NEXUS config */}
          <div className="border-t border-white/[0.06] pt-2.5 mt-2">
            <div className="flex items-center justify-between text-[10px] mb-1.5">
              <span className="text-white/45 uppercase tracking-wide">NEXUS</span>
              <RegimeChip regime={nexusRegime} />
            </div>
            <div className="grid grid-cols-2 gap-1.5 text-[10px]">
              <div className="flex justify-between">
                <span className="text-white/40">Paused</span>
                <span className={nexusPaused ? "text-rose-400" : "text-emerald-400"}>
                  {nexusPaused ? "YES" : "NO"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/40">Modifier</span>
                <span className="font-mono text-white/70">{nexusModifier.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ROW 2 — Hurst MTF + Physics Detectors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

        {/* Hurst Multi-TF Confluence */}
        <section className="arena-equity-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-amber-300" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-white/70">Hurst Confluence</span>
            </div>
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3].map(i => (
                <div key={i}
                     className="h-2.5 w-2.5 rounded-full transition-all duration-700"
                     style={{ background: i < confluenceScore ? "#4dffaf" : "rgba(255,255,255,0.1)" }} />
              ))}
              <span className="text-[10px] font-mono text-white/55 ml-1">{confluenceScore}/3</span>
            </div>
          </div>

          <div className="space-y-2.5 mb-3">
            {["1h", "4h", "1d"].map(tf => {
              const raw = hurst_mtf[tf];
              const val = typeof raw === "object" ? (raw?.hurst ?? 0) : (Number(raw) || 0);
              const cls = typeof raw === "object"
                ? (raw?.classification || "neutral")
                : val > 0.55 ? "trending" : val < 0.45 ? "ranging" : "neutral";
              return (
                <HurstBar key={tf} label={tf} value={val} classification={cls} />
              );
            })}
          </div>

          <div className="border-t border-white/[0.06] pt-2 text-[9px] text-white/40 uppercase tracking-wide">
            {confluenceScore >= 3 ? "All timeframes aligned — strong directional bias"
           : confluenceScore === 2 ? "Majority aligned — moderate structure"
           : confluenceScore === 1 ? "Fragmented — timeframes disagree"
           : "No confluence — market structure uncertain"}
          </div>
        </section>

        {/* Physics Anomaly Detectors */}
        <section className="arena-equity-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-rose-300" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-white/70">Anomaly Detectors</span>
            </div>
            <span className="text-[10px] font-mono text-white/45">{activeFlags.size} firing</span>
          </div>
          <div className="space-y-1">
            {allDetectors.map(name => (
              <DetectorDot key={name} name={name} active={activeFlags.has(name)} />
            ))}
          </div>
        </section>
      </div>

      {/* ROW 3 — MI Engine Weights */}
      <section className="arena-equity-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="h-3.5 w-3.5 text-emerald-300" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-white/70">Engine MI Weights · α formula</span>
          </div>
          <span className="text-[9px] text-white/35 font-mono">αᵢ = S×decay×regime×independence×MI</span>
        </div>
        {engineStates.length === 0 ? (
          <div className="text-center text-white/35 text-[11px] uppercase tracking-[0.22em] py-6">
            no engine data — wait 60s after startup
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {engineStates.map((es) => {
              const name = (es.engine || es.name || "unknown").replace(/_/g, " ");
              const alpha = Number(es.alpha || 0);
              const miWeight = Number(es.mi_weight ?? 1);
              const pnlCv = Number(es.pnl_cv ?? 0.5);
              const alphaColor = alpha >= 0 ? "#4dffaf" : "#ff5577";
              const miColor = miWeight >= 0.8 ? "#4dffaf" : miWeight >= 0.5 ? "#ffc24d" : "#ff5577";
              const alphaPct = Math.min(100, Math.abs(alpha) * 5);
              return (
                <div key={es.engine || es.name} className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5">
                  <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.18em] mb-2">
                    <span className="text-white/60 truncate">{name}</span>
                    <span className="font-mono font-semibold ml-1 flex-shrink-0" style={{ color: alphaColor }}>
                      α {alpha >= 0 ? "+" : ""}{alpha.toFixed(3)}
                    </span>
                  </div>
                  {/* Alpha bar */}
                  <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden mb-2">
                    <div className="h-full rounded-full transition-[width] duration-700"
                         style={{ width: `${alphaPct}%`, background: alphaColor, boxShadow: `0 0 6px ${alphaColor}` }} />
                  </div>
                  {/* MI + CV stats */}
                  <div className="flex gap-2 text-[9px]">
                    <div className="flex-1">
                      <div className="text-white/35 mb-0.5">MI weight</div>
                      <div className="font-mono" style={{ color: miColor }}>{miWeight.toFixed(3)}</div>
                    </div>
                    <div className="flex-1">
                      <div className="text-white/35 mb-0.5">PnL CV</div>
                      <div className="font-mono text-white/65">{pnlCv.toFixed(3)}</div>
                    </div>
                    {es.edge_score !== undefined && (
                      <div className="flex-1">
                        <div className="text-white/35 mb-0.5">Edge S</div>
                        <div className="font-mono text-white/65">{Number(es.edge_score).toFixed(3)}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

    </div>
  );
}
